import { Box, FormControlLabel, styled, Typography } from '@mui/material';
import {
  Control,
  FieldErrors,
  SubmitHandler,
  useForm,
  UseFormHandleSubmit,
  UseFormReset,
} from 'react-hook-form';
import { NumberFieldRHF } from 'src/components/forms/NumberInput';

import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { handleMutation } from 'src/utils/api.utils';
import { toast } from 'react-toastify';
import { ModalTitle } from '../../../components/CenteredModal';
import { FormLine, LowerCaseButton } from '../../../components/common';
import { TextFieldRHF } from '../../../components/forms/TextFieldRhf';
import FileSettings from '../../../components/icons/FileSettingsIcon';
import { Quote, updateQuoteComment } from '../../../services/quotes';
import muiTheme from '../../../themes/muiTheme';

export type CommentFormSchema = z.infer<typeof commentFormSchema>;

export const commentFormSchema = z.object({
  comment: z.string(),
});

interface ICommentForm {
  quote?: Quote;
  closeForm: () => void;
  onRefresh: () => void;
}

const StyledForm = styled('form')(({ theme }) => ({
  background: 'white',
  boxShadow: theme.shadows[1],
  borderRadius: '10px',
}));

const Body = styled(Box)({
  padding: '30px',
  display: 'flex',
  flexDirection: 'column',
  gap: '30px',
});

const CommentForm = ({ quote, closeForm, onRefresh }: ICommentForm) => {
  const { control, handleSubmit, reset } = useForm<CommentFormSchema>({
    defaultValues: {
      comment: quote?.comment || '',
    },
    resolver: zodResolver(commentFormSchema),
  });

  const onSubmitForm: SubmitHandler<CommentFormSchema> = (data) => {
    closeForm();

    if (quote?.assemblyId) {
      handleMutation(
        updateQuoteComment,
        {
          assemblyId: quote?.assemblyId,
          comment: data.comment,
        },
        () => {
          toast.success('Commentaire mis à jour');
          onRefresh();
        },
      );
    }
  };

  return (
    <StyledForm onSubmit={handleSubmit(onSubmitForm)}>
      <ModalTitle sx={quote ? { background: '#F7F0E7' } : {}}>
        <Typography
          sx={{
            color: quote ? muiTheme.palette.secondary.main : muiTheme.palette.primary.main,
            width: '100%',
            paddingLeft: '30px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}
          variant="h2"
          textAlign="left"
        >
          <FileSettings fontSize="large" />
          Paramètres du devis
        </Typography>
      </ModalTitle>
      <Body>
        <Typography>
          Les paramètres du devis sont modifiables à tout moment de la création du projet.
        </Typography>
        <FormLine>
          <TextFieldRHF
            control={control}
            name="comment"
            TextFieldProps={{
              label: 'Commentaire',
              color: quote ? 'secondary' : 'primary',
            }}
          />
        </FormLine>

        <FormLine>
          <div />
          <LowerCaseButton
            color={quote ? 'secondary' : 'primary'}
            onClick={() => {
              closeForm();
              reset();
            }}
          >
            {quote ? 'Annuler' : 'Passer cette étape'}
          </LowerCaseButton>
          <LowerCaseButton
            variant="contained"
            type="submit"
            color={quote ? 'secondary' : 'primary'}
          >
            {quote ? 'Valider' : 'Commencer'}
          </LowerCaseButton>
        </FormLine>
      </Body>
    </StyledForm>
  );
};

export default CommentForm;
